import { useEffect, useRef } from "react";
import { RouterProvider } from "react-router-dom";
import { clarity } from "react-microsoft-clarity";
import { AinaIntroPrimer } from "./components/aina-intro";
import { router } from "./routes/router";
import { AuthProvider } from "./providers/AuthProvider";
import { MenuNavigationProvider } from "./providers/MenuNavigationProvider";
import { clarityProjectId, isClarityEnabled } from "./lib/clarityIdentity";
import {
  initFormbricks,
  registerFormbricksRouteChange,
  syncFormbricksUserIdentity,
} from "./lib/formbricks";
import { useAuthStore } from "./store/useAuthStore";
import {
  isGeniusDeeplinkNavLockActive,
  isPathAllowedDuringGeniusNavLock,
} from "./components/deeplink/genius/geniusDeeplinkNavLock";
import {
  abandonGeniusDeeplinkIfAuthEscape,
  hasGeniusExpectResumeOnShell,
  isGeniusAppShellPath,
  isGeniusAuthEscapePath,
  shouldAutoResumeGeniusDeeplink,
  shouldEnforceGeniusBackTrap,
  shouldEnforceGeniusOtpBackTrap,
} from "./components/deeplink/genius/geniusDeeplinkSession";
import { isGeniusDeeplinkResumeInFlight } from "./components/deeplink/genius/resumeGeniusDeeplinkToDestination";
import { GeniusLogoutConfirmHost } from "./components/deeplink/genius/GeniusLogoutConfirmHost";
import { isGeniusDeeplinkEntryPath } from "./components/deeplink/genius/parseGeniusDeeplink";
import {
  handleGeniusRouterHistoryPop,
  promptGeniusLogoutConfirmForNavViolation,
  sealGeniusDeeplinkHistory,
  syncGeniusDeeplinkBackTrap,
} from "./components/deeplink/genius/geniusDeeplinkBackTrap";
import "./App.css";

function shouldEnforceGeniusUrlLock(): boolean {
  if (hasGeniusExpectResumeOnShell()) return false;
  if (isGeniusDeeplinkResumeInFlight()) return false;
  if (isGeniusDeeplinkNavLockActive()) return true;
  if (!shouldEnforceGeniusOtpBackTrap(useAuthStore.getState().profilesData)) {
    return false;
  }
  return shouldAutoResumeGeniusDeeplink(useAuthStore.getState().profilesData);
}

function isGeniusEntryPath(pathname: string): boolean {
  return isGeniusDeeplinkEntryPath(pathname);
}

function App() {
  const profilesData = useAuthStore((state) => state.profilesData);
  const token = useAuthStore((state) => state.token);
  const skipInitialPopRef = useRef(true);
  const lastPathnameRef = useRef("");

  useEffect(() => {
    if (!isClarityEnabled || !clarityProjectId) {
      return;
    }
    clarity.init(clarityProjectId);
  }, []);

  useEffect(() => {
    void (async () => {
      await initFormbricks();
      await registerFormbricksRouteChange();
    })();
  }, []);

  useEffect(() => {
    if (!token) {
      return;
    }
    void syncFormbricksUserIdentity(profilesData);
  }, [token, profilesData]);

  useEffect(() => {
    return router.subscribe(() => {
      void registerFormbricksRouteChange();
    });
  }, []);

  // Global SPA URL guard: leave allowed Genius paths → blank.
  useEffect(() => {
    return router.subscribe((state) => {
      if (!shouldEnforceGeniusUrlLock()) return;
      if (!useAuthStore.getState().token) return;

      const pathname = state.location.pathname;
      if (isGeniusEntryPath(pathname)) return;
      if (isGeniusAppShellPath(pathname)) {
        // Browser Back to home/dashboard must still show logout confirm.
        if (state.historyAction === "POP") {
          promptGeniusLogoutConfirmForNavViolation();
        }
        return;
      }
      // Login/intro — abandon Genius instead of blanking (API failure escape).
      if (isGeniusAuthEscapePath(pathname)) {
        abandonGeniusDeeplinkIfAuthEscape(pathname);
        return;
      }
      if (isPathAllowedDuringGeniusNavLock(pathname)) {
        if (state.historyAction === "POP" && isGeniusDeeplinkNavLockActive()) {
          promptGeniusLogoutConfirmForNavViolation();
        }
        return;
      }

      if (state.historyAction !== "POP") {
        return;
      }

      promptGeniusLogoutConfirmForNavViolation();
    });
  }, []);

  // Back → logout confirm while Genius deeplink nav lock / OTP is active.
  // First Genius entry is armed from main.tsx; keep re-sealing after SPA navigations.
  useEffect(() => {
    if (shouldEnforceGeniusBackTrap(profilesData)) {
      syncGeniusDeeplinkBackTrap(true);
    } else {
      syncGeniusDeeplinkBackTrap(false);
    }

    return router.subscribe((state) => {
      if (skipInitialPopRef.current) {
        skipInitialPopRef.current = false;
        lastPathnameRef.current = state.location.pathname;
        syncGeniusDeeplinkBackTrap(
          shouldEnforceGeniusBackTrap(useAuthStore.getState().profilesData),
        );
        return;
      }

      const geniusBackTrapActive = shouldEnforceGeniusBackTrap(
        useAuthStore.getState().profilesData,
      );
      if (!geniusBackTrapActive) {
        syncGeniusDeeplinkBackTrap(false);
        lastPathnameRef.current = state.location.pathname;
        return;
      }

      syncGeniusDeeplinkBackTrap(true);

      if (state.historyAction === "POP") {
        handleGeniusRouterHistoryPop();
        return;
      }

      // PUSH/REPLACE overwrote the seal — put it back so the next Back is ours.
      const pathname = state.location.pathname;
      if (pathname !== lastPathnameRef.current) {
        lastPathnameRef.current = pathname;
      }
      sealGeniusDeeplinkHistory();
    });
  }, [profilesData]);

  return (
    <AuthProvider>
      <MenuNavigationProvider>
        <AinaIntroPrimer />
        <GeniusLogoutConfirmHost />
        <RouterProvider router={router} />
      </MenuNavigationProvider>
    </AuthProvider>
  );
}

export default App;
